// File: src/app/services/kt-app-calendar.service.ts
import { Injectable, NgZone } from '@angular/core';
import Swal from 'sweetalert2';

/**
 * KTAppCalendarService
 * Angular/TS port of your KTAppCalendar module with robust null checks.
 *
 * Usage:
 *  - Ensure globals are loaded: FullCalendar, flatpickr, moment, bootstrap.Modal, FormValidation, SweetAlert2.
 *  - Place the calendar + modals in your component template.
 *  - Call `calendar.init()` in ngAfterViewInit.
 */
declare const FullCalendar: any;
declare const flatpickr: any;
declare const moment: any;
declare const bootstrap: any;
declare const FormValidation: any;

export interface CalendarSelectors {
	calendarEl: string;                 // '#kt_calendar_app'
	// Add Event modal
	addModalEl: string;                  // '#kt_modal_add_event'
	addFormEl: string;                   // '#kt_modal_add_event_form'
	addName: string;                     // '[name="calendar_event_name"]'
	addDesc: string;                     // '[name="calendar_event_description"]'
	addLoc: string;                      // '[name="calendar_event_location"]'
	addStartDate: string;                // '#kt_calendar_datepicker_start_date'
	addEndDate: string;                  // '#kt_calendar_datepicker_end_date'
	addStartTime: string;                // '#kt_calendar_datepicker_start_time'
	addEndTime: string;                  // '#kt_calendar_datepicker_end_time'
	addSubmit: string;                   // '#kt_modal_add_event_submit'
	addCancel: string;                   // '#kt_modal_add_event_cancel'
	addClose: string;                    // '#kt_modal_add_event_close'
	addTitle: string;                    // '[data-kt-calendar="title"]'
	addAllDayToggle: string;             // '#kt_calendar_datepicker_allday'
	addDatepickerWrappers: string;       // '[data-kt-calendar="datepicker"]'
	addButton: string;                   // '[data-kt-calendar="add"]'

	// View Event modal
	viewModalEl: string;                 // '#kt_modal_view_event'
	viewName: string;                    // '[data-kt-calendar="event_name"]'
	viewAllDay: string;                  // '[data-kt-calendar="all_day"]'
	viewDesc: string;                    // '[data-kt-calendar="event_description"]'
	viewLoc: string;                     // '[data-kt-calendar="event_location"]'
	viewStart: string;                   // '[data-kt-calendar="event_start_date"]'
	viewEnd: string;                     // '[data-kt-calendar="event_end_date"]'
	viewEditBtn: string;                 // '#kt_modal_view_event_edit'
	viewDeleteBtn: string;               // '#kt_modal_view_event_delete'

	// Optional: view button that opens the modal directly
	viewOpenBtn?: string;                // '#kt_calendar_event_view_button'
}

interface CalendarData {
	id: string;
	eventName: string;
	eventDescription: string;
	eventLocation: string;
	startDate: any;
	endDate: any;
	allDay: boolean;
}

@Injectable({ providedIn: 'root' })
export class KTAppCalendarService {
	// Calendar instance
	private calendar: any;

	// Shared data object
	private data: CalendarData = {
		id: '',
		eventName: '',
		eventDescription: '',
		eventLocation: '',
		startDate: '',
		endDate: '',
		allDay: false
	};

	// Selectors (with defaults)
	private s!: CalendarSelectors;

	// Add Event modal refs
	private addModal!: any;
	private addModalEl!: HTMLElement;
	private addForm!: HTMLFormElement;
	private addNameEl!: HTMLInputElement;
	private addDescEl!: HTMLInputElement | HTMLTextAreaElement;
	private addLocEl!: HTMLInputElement;
	private addStartDateEl!: HTMLElement;
	private addEndDateEl!: HTMLElement;
	private addStartTimeEl!: HTMLElement;
	private addEndTimeEl!: HTMLElement;
	private addSubmitBtn!: HTMLButtonElement;
	private addCancelBtn!: HTMLButtonElement;
	private addCloseBtn!: HTMLButtonElement;
	private addTitleEl!: HTMLElement;
	private addAllDayToggleEl!: HTMLInputElement;
	private validator!: any;

	// Flatpickr instances
	private startFlatpickr: any;
	private endFlatpickr: any;
	private startTimeFlatpickr: any;
	private endTimeFlatpickr: any;

	// View Event modal refs
	private viewModal!: any;
	private viewModalEl!: HTMLElement;
	private viewNameEl!: HTMLElement;
	private viewAllDayEl!: HTMLElement;
	private viewDescEl!: HTMLElement;
	private viewLocEl!: HTMLElement;
	private viewStartEl!: HTMLElement;
	private viewEndEl!: HTMLElement;
	private viewEditBtn!: HTMLButtonElement;
	private viewDeleteBtn!: HTMLButtonElement;

	constructor(private zone: NgZone) { }

	//#region PUBLIC API ---------------------------------------------------------
	/** Initialize calendar + modals. Call this from ngAfterViewInit. */
	public init(custom?: Partial<CalendarSelectors>): void {
		this.s = {
			calendarEl: '#kt_calendar_app',
			addModalEl: '#kt_modal_add_event',
			addFormEl: '#kt_modal_add_event_form',
			addName: '[name="calendar_event_name"]',
			addDesc: '[name="calendar_event_description"]',
			addLoc: '[name="calendar_event_location"]',
			addStartDate: '#kt_calendar_datepicker_start_date',
			addEndDate: '#kt_calendar_datepicker_end_date',
			addStartTime: '#kt_calendar_datepicker_start_time',
			addEndTime: '#kt_calendar_datepicker_end_time',
			addSubmit: '#kt_modal_add_event_submit',
			addCancel: '#kt_modal_add_event_cancel',
			addClose: '#kt_modal_add_event_close',
			addTitle: '[data-kt-calendar="title"]',
			addAllDayToggle: '#kt_calendar_datepicker_allday',
			addDatepickerWrappers: '[data-kt-calendar="datepicker"]',
			addButton: '[data-kt-calendar="add"]',
			viewModalEl: '#kt_modal_view_event',
			viewName: '[data-kt-calendar="event_name"]',
			viewAllDay: '[data-kt-calendar="all_day"]',
			viewDesc: '[data-kt-calendar="event_description"]',
			viewLoc: '[data-kt-calendar="event_location"]',
			viewStart: '[data-kt-calendar="event_start_date"]',
			viewEnd: '[data-kt-calendar="event_end_date"]',
			viewEditBtn: '#kt_modal_view_event_edit',
			viewDeleteBtn: '#kt_modal_view_event_delete',
			...custom
		};

		this.zone.runOutsideAngular(() => {
			const calEl = document.querySelector(this.s.calendarEl) as HTMLElement | null;
			this.addModalEl = document.querySelector(this.s.addModalEl) as HTMLElement;
			const form = this.addModalEl?.querySelector(this.s.addFormEl) as HTMLFormElement | null;

			if (!calEl || !this.addModalEl || !form) {
				console.warn('Calendar/modal/form missing. Ensure DOM is rendered before init().');
				return;
			}

			// cache add-form elements
			this.addForm = form;
			this.addNameEl = form.querySelector(this.s.addName) as HTMLInputElement;
			this.addDescEl = form.querySelector(this.s.addDesc) as HTMLInputElement | HTMLTextAreaElement;
			this.addLocEl = form.querySelector(this.s.addLoc) as HTMLInputElement;
			this.addStartDateEl = form.querySelector(this.s.addStartDate) as HTMLElement;
			this.addEndDateEl = form.querySelector(this.s.addEndDate) as HTMLElement;
			this.addStartTimeEl = form.querySelector(this.s.addStartTime) as HTMLElement;
			this.addEndTimeEl = form.querySelector(this.s.addEndTime) as HTMLElement;
			this.addSubmitBtn = form.querySelector(this.s.addSubmit) as HTMLButtonElement;
			this.addCancelBtn = form.querySelector(this.s.addCancel) as HTMLButtonElement;
			this.addCloseBtn = this.addModalEl.querySelector(this.s.addClose) as HTMLButtonElement;
			this.addTitleEl = form.querySelector(this.s.addTitle) as HTMLElement;
			this.addAllDayToggleEl = form.querySelector(this.s.addAllDayToggle) as HTMLInputElement;

			// view modal
			this.viewModalEl = document.querySelector(this.s.viewModalEl) as HTMLElement;
			if (this.viewModalEl) {
				this.viewModal = new bootstrap.Modal(this.viewModalEl);
				this.viewNameEl = this.viewModalEl.querySelector(this.s.viewName) as HTMLElement;
				this.viewAllDayEl = this.viewModalEl.querySelector(this.s.viewAllDay) as HTMLElement;
				this.viewDescEl = this.viewModalEl.querySelector(this.s.viewDesc) as HTMLElement;
				this.viewLocEl = this.viewModalEl.querySelector(this.s.viewLoc) as HTMLElement;
				this.viewStartEl = this.viewModalEl.querySelector(this.s.viewStart) as HTMLElement;
				this.viewEndEl = this.viewModalEl.querySelector(this.s.viewEnd) as HTMLElement;
				this.viewEditBtn = this.viewModalEl.querySelector(this.s.viewEditBtn) as HTMLButtonElement;
				this.viewDeleteBtn = this.viewModalEl.querySelector(this.s.viewDeleteBtn) as HTMLButtonElement;
			}

			// bootstrap modal + features
			this.addModal = new bootstrap.Modal(this.addModalEl);
			this.initCalendarApp(calEl);
			this.initValidator();
			this.initDatepickers();
			this.handleEditButton();
			this.handleAddButton();
			this.handleDeleteEvent();
			this.handleCancelButton();
			this.handleCloseButton();
			this.resetFormValidator(this.addModalEl);
		});
	}

	/** Optional: hook up a standalone "view event" button if you use one. */
	public handleViewButton(selector = '#kt_calendar_event_view_button'): void {
		const viewButton = document.querySelector(selector) as HTMLButtonElement | null;
		if (!viewButton) return;
		viewButton.addEventListener('click', (e) => {
			e.preventDefault();
			this.hidePopovers();
			this.handleViewEvent();
		});
	}
	//#endregion PUBLIC API ------------------------------------------------------

	//#region INTERNALS (ported 1:1 from your JS) --------------------------------
	private initCalendarApp(calendarEl: HTMLElement): void {
		const todayDate = moment().startOf('day');
		const YM = todayDate.format('YYYY-MM');
		const YESTERDAY = todayDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
		const TODAY = todayDate.format('YYYY-MM-DD');
		const TOMORROW = todayDate.clone().add(1, 'day').format('YYYY-MM-DD');

		this.calendar = new FullCalendar.Calendar(calendarEl, {
			headerToolbar: { left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' },
			initialDate: TODAY,
			navLinks: true,
			selectable: true,
			selectMirror: true,
			select: (arg: any) => { this.formatArgs(arg); this.handleNewEvent(); },
			eventClick: (arg: any) => {
				this.formatArgs({
					id: arg.event.id,
					title: arg.event.title,
					description: arg.event.extendedProps.description,
					location: arg.event.extendedProps.location,
					startStr: arg.event.startStr,
					endStr: arg.event.endStr,
					allDay: arg.event.allDay
				});
				this.handleViewEvent();
			},
			editable: true,
			dayMaxEvents: true,
			events: [
				{ id: this.uid(), title: 'All Day Event', start: `${YM}-01`, end: `${YM}-02`, description: 'Toto lorem ipsum dolor sit incid idunt ut', className: 'border-success bg-success text-inverse-success', location: 'Federation Square' },
				{ id: this.uid(), title: 'Reporting', start: `${YM}-14T13:30:00`, end: `${YM}-14T14:30:00`, description: 'Lorem ipsum dolor incid idunt ut labore', className: 'border-warning bg-warning text-inverse-success', location: 'Meeting Room 7.03' },
				{ id: this.uid(), title: 'Company Trip', start: `${YM}-02`, end: `${YM}-03`, description: 'Lorem ipsum dolor sit tempor incid', className: 'border-info bg-info text-info-success', location: 'Seoul, Korea' },
				{ id: this.uid(), title: 'ICT Expo 2021 - Product Release', start: `${YM}-03`, end: `${YM}-05`, description: 'Lorem ipsum dolor sit tempor inci', className: 'fc-event-light fc-event-solid-primary', location: 'Melbourne Exhibition Hall' },
				{ id: this.uid(), title: 'Dinner', start: `${YM}-12`, end: `${YM}-13`, description: 'Lorem ipsum dolor sit amet, conse ctetur', location: "Squire's Loft" },
				{ id: this.uid(), title: 'Repeating Event', start: `${YM}-09T16:00:00`, end: `${YM}-09T17:00:00`, description: 'Lorem ipsum dolor sit ncididunt ut labore', className: 'fc-event-danger', location: 'General Area' },
				{ id: this.uid(), title: 'Repeating Event', start: `${YM}-16T16:00:00`, end: `${YM}-16T17:00:00`, description: 'Lorem ipsum dolor sit amet, labore', location: 'General Area' },
				{ id: this.uid(), title: 'Conference', start: YESTERDAY, end: TOMORROW, description: 'Lorem ipsum dolor eius mod tempor labore', className: 'fc-event-primary', location: 'Conference Hall A' },
				{ id: this.uid(), title: 'Meeting', start: `${TODAY}T10:30:00`, end: `${TODAY}T12:30:00`, description: 'Lorem ipsum dolor eiu idunt ut labore', location: 'Meeting Room 11.06' },
				{ id: this.uid(), title: 'Lunch', start: `${TODAY}T12:00:00`, end: `${TODAY}T14:00:00`, description: 'Lorem ipsum dolor sit amet, ut labore', className: 'fc-event-info', location: 'Cafeteria' },
				{ id: this.uid(), title: 'Meeting', start: `${TODAY}T14:30:00`, end: `${TODAY}T15:30:00`, description: 'Lorem ipsum conse ctetur adipi scing', className: 'fc-event-warning', location: 'Meeting Room 11.10' },
				{ id: this.uid(), title: 'Happy Hour', start: `${TODAY}T17:30:00`, end: `${TODAY}T21:30:00`, description: 'Lorem ipsum dolor sit amet, conse ctetur', className: 'fc-event-info', location: 'The English Pub' },
				{ id: this.uid(), title: 'Dinner', start: `${TOMORROW}T18:00:00`, end: `${TOMORROW}T21:00:00`, description: 'Lorem ipsum dolor sit ctetur adipi scing', className: 'fc-event-solid-danger fc-event-light', location: 'New York Steakhouse' },
				{ id: this.uid(), title: 'Birthday Party', start: `${TOMORROW}T12:00:00`, end: `${TOMORROW}T14:00:00`, description: 'Lorem ipsum dolor sit amet, scing', className: 'fc-event-primary', location: 'The English Pub' },
				{ id: this.uid(), title: 'Site visit', start: `${YM}-28`, end: `${YM}-29`, description: 'Lorem ipsum dolor sit amet, labore', className: 'fc-event-solid-info fc-event-light', location: '271, Spring Street' }
			],
			datesSet: () => { /* hook if needed */ }
		});

		this.calendar.render();
	}

	private initValidator(): void {
		this.validator = FormValidation.formValidation(this.addForm, {
			fields: {
				'calendar_event_name': { validators: { notEmpty: { message: 'Event name is required' } } },
				'calendar_event_start_date': { validators: { notEmpty: { message: 'Start date is required' } } },
				'calendar_event_end_date': { validators: { notEmpty: { message: 'End date is required' } } }
			},
			plugins: {
				trigger: new FormValidation.plugins.Trigger(),
				bootstrap: new FormValidation.plugins.Bootstrap5({
					rowSelector: '.fv-row',
					eleInvalidClass: '',
					eleValidClass: ''
				})
			}
		});
	}

	private initDatepickers(): void {
		if (this.addStartDateEl) this.startFlatpickr = flatpickr(this.addStartDateEl, { enableTime: false, dateFormat: 'Y-m-d' });
		if (this.addEndDateEl) this.endFlatpickr = flatpickr(this.addEndDateEl, { enableTime: false, dateFormat: 'Y-m-d' });
		if (this.addStartTimeEl) this.startTimeFlatpickr = flatpickr(this.addStartTimeEl, { enableTime: true, noCalendar: true, dateFormat: 'H:i' });
		if (this.addEndTimeEl) this.endTimeFlatpickr = flatpickr(this.addEndTimeEl, { enableTime: true, noCalendar: true, dateFormat: 'H:i' });
	}

	private handleAddButton(): void {
		const addBtn = document.querySelector(this.s.addButton) as HTMLButtonElement | null;
		if (!addBtn) return;

		addBtn.addEventListener('click', (e) => {
			e.preventDefault();
			this.data = {
				id: '',
				eventName: '',
				eventDescription: '',
				eventLocation: '',
				startDate: new Date(),
				endDate: new Date(),
				allDay: false
			};
			this.handleNewEvent();
		});
	}

	private handleNewEvent(): void {
		if (!this.addTitleEl) return;
		this.addTitleEl.innerText = 'Add a New Event';
		this.addModal.show();

		const datepickerWrappers = this.addForm.querySelectorAll(this.s.addDatepickerWrappers);
		const allDayToggle = this.addForm.querySelector(this.s.addAllDayToggle) as HTMLInputElement;

		allDayToggle?.addEventListener('click', (e: any) => {
			if (e.target.checked) {
				datepickerWrappers.forEach((dw) => dw.classList.add('d-none'));
			} else {
				this.endFlatpickr?.setDate(this.data.startDate, true, 'Y-m-d');
				datepickerWrappers.forEach((dw) => dw.classList.remove('d-none'));
			}
		}, { once: true });

		this.populateForm();

		// avoid duplicate handlers
		this.addSubmitBtn = this.replaceWithClone(this.addSubmitBtn);
		this.addSubmitBtn.addEventListener('click', (e) => {
			e.preventDefault();
			if (!this.validator) return;

			this.validator.validate().then((status: string) => {
				if (status === 'Valid') {
					this.addSubmitBtn.setAttribute('data-kt-indicator', 'on');
					this.addSubmitBtn.disabled = true;

					setTimeout(() => {
						this.addSubmitBtn.removeAttribute('data-kt-indicator');

						Swal.fire({
							text: 'New event added to calendar!',
							icon: 'success',
							buttonsStyling: false,
							confirmButtonText: 'Ok, got it!',
							customClass: { confirmButton: 'btn btn-primary' }
						}).then((result) => {
							if (result.isConfirmed) {
								this.addModal.hide();
								this.addSubmitBtn.disabled = false;

								let allDayEvent = false;
								if (allDayToggle?.checked) allDayEvent = true;
								if (!this.startTimeFlatpickr || this.startTimeFlatpickr.selectedDates.length === 0) allDayEvent = true;

								let startDateTime = moment(this.startFlatpickr?.selectedDates[0]).format();
								let endDateTime = moment(this.endFlatpickr?.selectedDates[this.endFlatpickr?.selectedDates.length - 1]).format();

								if (!allDayEvent) {
									const startDate = moment(this.startFlatpickr?.selectedDates[0]).format('YYYY-MM-DD');
									const endDate = startDate;
									const startTime = moment(this.startTimeFlatpickr?.selectedDates[0]).format('HH:mm:ss');
									const endTime = moment(this.endTimeFlatpickr?.selectedDates[0]).format('HH:mm:ss');
									startDateTime = `${startDate}T${startTime}`;
									endDateTime = `${endDate}T${endTime}`;
								}

								this.calendar.addEvent({
									id: this.uid(),
									title: this.addNameEl?.value,
									description: (this.addDescEl as any)?.value,
									location: this.addLocEl?.value,
									start: startDateTime,
									end: endDateTime,
									allDay: allDayEvent
								});
								this.calendar.render();
								this.addForm.reset();
							}
						});
					}, 2000);
				} else {
					Swal.fire({
						text: 'Sorry, looks like there are some errors detected, please try again.',
						icon: 'error',
						buttonsStyling: false,
						confirmButtonText: 'Ok, got it!',
						customClass: { confirmButton: 'btn btn-primary' }
					});
				}
			});
		});
	}

	private handleEditEvent(): void {
		if (!this.addTitleEl) return;
		this.addTitleEl.innerText = 'Edit an Event';
		this.addModal.show();

		const datepickerWrappers = this.addForm.querySelectorAll(this.s.addDatepickerWrappers);
		const allDayToggle = this.addForm.querySelector(this.s.addAllDayToggle) as HTMLInputElement;

		allDayToggle?.addEventListener('click', (e: any) => {
			if (e.target.checked) {
				datepickerWrappers.forEach((dw) => dw.classList.add('d-none'));
			} else {
				this.endFlatpickr?.setDate(this.data.startDate, true, 'Y-m-d');
				datepickerWrappers.forEach((dw) => dw.classList.remove('d-none'));
			}
		}, { once: true });

		this.populateForm();

		this.addSubmitBtn = this.replaceWithClone(this.addSubmitBtn);
		this.addSubmitBtn.addEventListener('click', (e) => {
			e.preventDefault();
			if (!this.validator) return;

			this.validator.validate().then((status: string) => {
				if (status === 'Valid') {
					this.addSubmitBtn.setAttribute('data-kt-indicator', 'on');
					this.addSubmitBtn.disabled = true;

					setTimeout(() => {
						this.addSubmitBtn.removeAttribute('data-kt-indicator');

						Swal.fire({
							text: 'New event added to calendar!',
							icon: 'success',
							buttonsStyling: false,
							confirmButtonText: 'Ok, got it!',
							customClass: { confirmButton: 'btn btn-primary' }
						}).then((result) => {
							if (result.isConfirmed) {
								this.addModal.hide();
								this.addSubmitBtn.disabled = false;

								this.calendar.getEventById(this.data.id)?.remove();

								let allDayEvent = false;
								if (allDayToggle?.checked) allDayEvent = true;
								if (!this.startTimeFlatpickr || this.startTimeFlatpickr.selectedDates.length === 0) allDayEvent = true;

								let startDateTime = moment(this.startFlatpickr?.selectedDates[0]).format();
								let endDateTime = moment(this.endFlatpickr?.selectedDates[this.endFlatpickr?.selectedDates.length - 1]).format();

								if (!allDayEvent) {
									const startDate = moment(this.startFlatpickr?.selectedDates[0]).format('YYYY-MM-DD');
									const endDate = startDate;
									const startTime = moment(this.startTimeFlatpickr?.selectedDates[0]).format('HH:mm:ss');
									const endTime = moment(this.endTimeFlatpickr?.selectedDates[0]).format('HH:mm:ss');
									startDateTime = `${startDate}T${startTime}`;
									endDateTime = `${endDate}T${endTime}`;
								}

								this.calendar.addEvent({
									id: this.uid(),
									title: this.addNameEl?.value,
									description: (this.addDescEl as any)?.value,
									location: this.addLocEl?.value,
									start: startDateTime,
									end: endDateTime,
									allDay: allDayEvent
								});
								this.calendar.render();
								this.addForm.reset();
							}
						});
					}, 2000);
				} else {
					Swal.fire({
						text: 'Sorry, looks like there are some errors detected, please try again.',
						icon: 'error',
						buttonsStyling: false,
						confirmButtonText: 'Ok, got it!',
						customClass: { confirmButton: 'btn btn-primary' }
					});
				}
			});
		});
	}

	private handleViewEvent(): void {
		if (!this.viewModal) return;
		this.viewModal.show();

		let eventNameMod = '';
		let startDateMod = '';
		let endDateMod = '';

		if (this.data.allDay) {
			eventNameMod = 'All Day';
			startDateMod = moment(this.data.startDate).format('Do MMM, YYYY');
			endDateMod = moment(this.data.endDate).format('Do MMM, YYYY');
		} else {
			startDateMod = moment(this.data.startDate).format('Do MMM, YYYY - h:mm a');
			endDateMod = moment(this.data.endDate).format('Do MMM, YYYY - h:mm a');
		}

		if (this.viewNameEl) this.viewNameEl.innerText = this.data.eventName;
		if (this.viewAllDayEl) this.viewAllDayEl.innerText = eventNameMod;
		if (this.viewDescEl) this.viewDescEl.innerText = this.data.eventDescription || '--';
		if (this.viewLocEl) this.viewLocEl.innerText = this.data.eventLocation || '--';
		if (this.viewStartEl) this.viewStartEl.innerText = startDateMod;
		if (this.viewEndEl) this.viewEndEl.innerText = endDateMod;
	}

	private handleDeleteEvent(): void {
		if (!this.viewDeleteBtn) return;
		this.viewDeleteBtn.addEventListener('click', (e) => {
			e.preventDefault();
			Swal.fire({
				text: 'Are you sure you would like to delete this event?',
				icon: 'warning',
				showCancelButton: true,
				buttonsStyling: false,
				confirmButtonText: 'Yes, delete it!',
				cancelButtonText: 'No, return',
				customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-active-light' }
			}).then((result: any) => {
				if (result.value) {
					this.calendar.getEventById(this.data.id)?.remove();
					this.viewModal?.hide();
				} else if (result.dismiss === 'cancel') {
					Swal.fire({
						text: 'Your event was not deleted!.',
						icon: 'error',
						buttonsStyling: false,
						confirmButtonText: 'Ok, got it!',
						customClass: { confirmButton: 'btn btn-primary' }
					});
				}
			});
		});
	}

	private handleEditButton(): void {
		if (!this.viewEditBtn) return;
		this.viewEditBtn.addEventListener('click', (e) => {
			e.preventDefault();
			this.viewModal?.hide();
			this.handleEditEvent();
		});
	}

	private handleCancelButton(): void {
		if (!this.addCancelBtn) return;
		this.addCancelBtn.addEventListener('click', (e) => {
			e.preventDefault();
			Swal.fire({
				text: 'Are you sure you would like to cancel?',
				icon: 'warning',
				showCancelButton: true,
				buttonsStyling: false,
				confirmButtonText: 'Yes, cancel it!',
				cancelButtonText: 'No, return',
				customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-active-light' }
			}).then((result: any) => {
				if (result.value) {
					this.addForm.reset();
					this.addModal.hide();
				} else if (result.dismiss === 'cancel') {
					Swal.fire({
						text: 'Your form has not been cancelled!.',
						icon: 'error',
						buttonsStyling: false,
						confirmButtonText: 'Ok, got it!',
						customClass: { confirmButton: 'btn btn-primary' }
					});
				}
			});
		});
	}

	private handleCloseButton(): void {
		if (!this.addCloseBtn) return;
		this.addCloseBtn.addEventListener('click', (e) => {
			e.preventDefault();
			Swal.fire({
				text: 'Are you sure you would like to cancel?',
				icon: 'warning',
				showCancelButton: true,
				buttonsStyling: false,
				confirmButtonText: 'Yes, cancel it!',
				cancelButtonText: 'No, return',
				customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-active-light' }
			}).then((result: any) => {
				if (result.value) {
					this.addForm.reset();
					this.addModal.hide();
				} else if (result.dismiss === 'cancel') {
					Swal.fire({
						text: 'Your form has not been cancelled!.',
						icon: 'error',
						buttonsStyling: false,
						confirmButtonText: 'Ok, got it!',
						customClass: { confirmButton: 'btn btn-primary' }
					});
				}
			});
		});
	}
	//#endregion INTERNALS ------------------------------------------------------

	//#region HELPERS ------------------------------------------------------------
	private resetFormValidator(element: HTMLElement): void {
		element.addEventListener('hidden.bs.modal', () => {
			if (this.validator) this.validator.resetForm(true);
		});
	}

	private populateForm(): void {
		if (this.addNameEl) this.addNameEl.value = this.data.eventName || '';
		if (this.addDescEl) (this.addDescEl as any).value = this.data.eventDescription || '';
		if (this.addLocEl) this.addLocEl.value = this.data.eventLocation || '';

		this.startFlatpickr?.setDate(this.data.startDate, true, 'Y-m-d');
		const endDate = this.data.endDate ? this.data.endDate : moment(this.data.startDate).format();
		this.endFlatpickr?.setDate(endDate, true, 'Y-m-d');

		const allDayToggle = this.addForm.querySelector(this.s.addAllDayToggle) as HTMLInputElement;
		const datepickerWrappers = this.addForm.querySelectorAll(this.s.addDatepickerWrappers);

		if (this.data.allDay) {
			if (allDayToggle) allDayToggle.checked = true;
			datepickerWrappers.forEach((dw) => dw.classList.add('d-none'));
		} else {
			this.startTimeFlatpickr?.setDate(this.data.startDate, true, 'Y-m-d H:i');
			this.endTimeFlatpickr?.setDate(this.data.endDate, true, 'Y-m-d H:i');
			this.endFlatpickr?.setDate(this.data.startDate, true, 'Y-m-d');
			if (allDayToggle) allDayToggle.checked = false;
			datepickerWrappers.forEach((dw) => dw.classList.remove('d-none'));
		}
	}

	private formatArgs(res: any): void {
		this.data.id = res.id;
		this.data.eventName = res.title;
		this.data.eventDescription = res.description;
		this.data.eventLocation = res.location;
		this.data.startDate = res.startStr;
		this.data.endDate = res.endStr;
		this.data.allDay = res.allDay;
	}

	private uid(): string {
		return Date.now().toString() + Math.floor(Math.random() * 1000).toString();
	}

	private hidePopovers(): void {
		document.querySelectorAll('.popover').forEach((p) => p.parentElement?.removeChild(p));
	}

	/** Replace a node by its clone to remove previous listeners */
	private replaceWithClone<T extends HTMLElement>(el: T): T {
		const newEl = el.cloneNode(true) as T;
		el.parentNode?.replaceChild(newEl, el);
		return newEl;
	}
	//#endregion HELPERS ---------------------------------------------------------
}
